import { Stack, FormGroup, FormRow } from '@cad-arc';
import { ConsolidatedLocationView, EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import {
    AgencyFieldsDisplayRow,
    AgencyFormGroupEditable,
} from '../../panels/entity-panels/entity-panel-components';
import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { SubdivisionFields } from './event-panel-form-subdivision-fields';
import { globalAttributes } from '../../common/global-attributes';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useAgencyOptions } from './use-agency-options';

type EventPanelFormPoliceFieldsProps = {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
};

function useValuesFromProps(props: EventPanelFormPoliceFieldsProps) {
    const callForServiceValue = useWatch({
        control: props.form.control,
        name: 'callForService',
    });

    const location = useWatch({
        control: props.form.control,
        name: 'location',
        exact: true,
    }) as ConsolidatedLocationView | null;

    const agencyOptions = useAgencyOptions({
        location,
        baseOptions: props.eventPanelOptions.policeAgencyProfileOptions,
        agencyType: globalAttributes.agencyTypeGlobal.POLICE,
    });

    const eventForm = props.form;
    const eventPanelOptions = props.eventPanelOptions;

    const cfsIncludesPolice = callForServiceValue?.agencyTypeGlobalAttrIds.includes(
        globalAttributes.agencyTypeGlobal.POLICE
    );
    return {
        eventForm,
        eventPanelOptions,
        agencyOptions,
        callForServiceValue,
        cfsIncludesPolice,
    };
}

function DAreaAgencyFields({
    asDataListItem,
    ...props
}: EventPanelFormPoliceFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const localProps = useValuesFromProps({ ...props });

    return (
        <>
            <FormSelect
                asEditable={false}
                asDataListItem={asDataListItem}
                length="sm"
                control={localProps.eventForm.control}
                label="D-Area"
                name="policeDispatchArea"
                eventNotificationName="DISPATCH_AREA"
                options={localProps.eventPanelOptions.policeDispatchAreaOptions}
                showRequiredIndicator={!!localProps.cfsIncludesPolice}
            />
            <FormSelect
                control={localProps.eventForm.control}
                asEditable={false}
                asDataListItem={asDataListItem}
                label="Agency"
                name="policeAgency"
                eventNotificationName="AGENCY"
                options={localProps.agencyOptions}
                showRequiredIndicator={!!localProps.cfsIncludesPolice}
                length="md"
            />
        </>
    );
}

function RadioField({
    asDataListItem,
    ...props
}: EventPanelFormPoliceFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const localProps = useValuesFromProps({ ...props });

    if (enabledFeatures.radioChannels) {
        return (
            <FormSelect
                asEditable={false}
                asDataListItem={asDataListItem}
                control={localProps.eventForm.control}
                length="md"
                label="Radio Channel"
                name="policePrimaryRadioChannel"
                eventNotificationName="RADIO_CHANNEL"
                options={props.eventPanelOptions.radioChannelOptions}
            />
        );
    }
    return <></>;
}

function PoliceFields({
    asDataListItem,
    ...props
}: EventPanelFormPoliceFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const localProps = useValuesFromProps({ ...props });

    return asDataListItem ? (
        <Stack spacing={0}>
            <AgencyFieldsDisplayRow>
                <SubdivisionFields
                    asDataListItem
                    eventClassPrefix="police"
                    form={localProps.eventForm}
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <DAreaAgencyFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <RadioField
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            </AgencyFieldsDisplayRow>
        </Stack>
    ) : (
        <FormGroup spacing={1}>
            <FormRow>
                <SubdivisionFields eventClassPrefix="police" form={localProps.eventForm} />
            </FormRow>
            <FormRow>
                <DAreaAgencyFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            </FormRow>
            <FormRow>
                <RadioField
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            </FormRow>
        </FormGroup>
    );
}

export function EventPanelFormPoliceFields(
    props: EventPanelFormPoliceFieldsProps
): React.ReactElement {
    const localProps = useValuesFromProps(props);

    return (
        <AgencyFormGroupEditable
            agency="Police"
            viewMode={
                <PoliceFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            }
            editMode={
                <PoliceFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            }
        />
    );
}
